// ─────────────────────────────────────────────────────────────────────────────
// SDMS Result Validation — Batch Results row category.
//
// The legacy single "Batch Results" tab is split into four category tabs
// (Sample Results · QC Sample Results · Surrogate · Internal Standard). Every
// (sample × analyte) row belongs to exactly ONE bucket, decided WITHOUT the
// reviewer choosing. This module is the PURE decision layer (standing rule 8):
// it reuses the QC-type classification the Trend router already reads
// (`trendRouting.classifyRootRole`) so Sample-vs-QC never drifts between the tab
// split and the Trend icon. It is kept dependency-free so the buckets can be
// unit-tested without rendering the 4k-line SdmsPackageDetail grid — see
// resultCategory.test.ts.
//
// Precedence (first match wins):
//   1. Internal Standard — Testparameter.InternalStandard (an IS rides on every
//      sample but is not a reported analyte).
//   2. Surrogate         — se.uqTestSurrogateID = 1 (a spiked recovery check).
//   3. Sample Results    — a real client/field sample (QC-type chain root 'Sample').
//   4. QC Sample Results — every QC control (Blank / Standard / Spike / Duplicate),
//      i.e. anything not classified as a client Sample.
//
// The name/site FALLBACK only fires when the QC-type chain is absent (the demo
// seed, or a row with no configured QCType): a client sample carries a sampling
// location or client sample name; a QC control does not. On live data the chain
// is always present, so the fallback never runs — no fragile heuristic on real rows.
// ─────────────────────────────────────────────────────────────────────────────
import { classifyRootRole } from "../trend/trendRouting";

/** The four Batch Results categories, each its own tab. */
export type ResultCategory = "sample" | "qc" | "surr" | "is";

/** The minimal row shape the categoriser reads — the two lab flags, the QC-type
 *  chain fields classifyRootRole walks, and the sample-identity fallback fields. */
export interface CategoryRow {
  isInternalStandard?: boolean | null;
  isSurrogate?: boolean | null;
  qcRootRole?: string | null;
  qcRole?: string | null;
  qcTypeName?: string | null;
  siteName?: string | null;
  sampleName?: string | null;
}

/** Which category tab a Batch Results row belongs to. */
export function categorizeRow(r: CategoryRow): ResultCategory {
  if (r.isInternalStandard) return "is";
  if (r.isSurrogate) return "surr";
  const root = classifyRootRole({
    qcRootRole: r.qcRootRole ?? null,
    qcRole: r.qcRole ?? null,
    qcTypeName: r.qcTypeName ?? null,
  });
  if (root) return root === "Sample" ? "sample" : "qc";
  return (r.siteName ?? "").trim() || (r.sampleName ?? "").trim() ? "sample" : "qc";
}
